import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";
import {
    UserCheck,
    UserMinus,
    Clock,
    Search,
    Plus,
    Trash2,
    X,
    TrendingUp,
    Activity,
    Send,
    Globe,
} from "lucide-react";

const STATUS_TABS = [
    { id: "active", label: "Active", icon: UserCheck, color: "text-electric" },
    { id: "pending", label: "Pending Confirmation", icon: Clock, color: "text-yellow-400" },
    { id: "opted_out", label: "Opted Out", icon: UserMinus, color: "text-white/50" },
    { id: "all", label: "All", icon: Activity, color: "text-blue-400" },
];

const CHANNEL_TABS = [
    { id: "all", label: "All Channels" },
    { id: "sms", label: "SMS" },
    { id: "web", label: "Web" },
    { id: "admin", label: "Admin" },
];

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short", year: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const Subscriptions = () => {
    const { token } = useAuth();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ totals: { active: 0, pending: 0, opted_out: 0 }, by_team: [] });
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("active");
    const [channel, setChannel] = useState("all");
    const [search, setSearch] = useState("");
    const [team, setTeam] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ phone_number: "", team: "" });
    const [profilePhone, setProfilePhone] = useState(null);

    const authH = () => ({ Authorization: `Bearer ${token}` });

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ status });
            if (search) params.set("phone", search);
            if (team) params.set("team", team);
            if (channel !== "all") params.set("source", channel);
            const [subs, st] = await Promise.all([
                axios.get(`${API}/admin/subscriptions?${params.toString()}`, { headers: authH() }),
                axios.get(`${API}/admin/subscriptions/stats`, { headers: authH() }),
            ]);
            setItems(subs.data || []);
            setStats(st.data || { totals: { active: 0, pending: 0, opted_out: 0 }, by_team: [], by_source: { sms: 0, web: 0, admin: 0 } });
        } catch { toast.error("Failed to load"); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, [status, team, channel]);

    const handleSearch = (e) => { e.preventDefault(); fetchData(); };

    const handleAdd = async () => {
        if (!form.phone_number || !form.team) {
            toast.error("Phone and team required"); return;
        }
        try {
            await axios.post(`${API}/subscriptions`, { ...form, source: "admin" }, { headers: authH() });
            toast.success("Added & activated");
            setShowAdd(false);
            setForm({ phone_number: "", team: "" });
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const handleOptOut = async (id) => {
        if (!window.confirm("Force opt-out this subscriber?")) return;
        try {
            await axios.post(`${API}/admin/subscriptions/${id}/opt-out`, {}, { headers: authH() });
            toast.success("Opted out");
            fetchData();
        } catch { toast.error("Failed"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Permanently delete this subscription record?")) return;
        try {
            await axios.delete(`${API}/admin/subscriptions/${id}`, { headers: authH() });
            toast.success("Deleted");
            fetchData();
        } catch { toast.error("Failed"); }
    };

    return (
        <AdminLayout title="Subscriptions">
            <div className="space-y-6" data-testid="subscriptions-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <UserCheck className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">Subscriptions</h1>
                            <p className="text-white/50 text-sm">
                                Real-time SMS subscribers by team. Active subs auto-receive goals, cards, and match updates — no approval needed.
                            </p>
                        </div>
                    </div>
                    <Button onClick={() => setShowAdd(true)} className="bg-electric hover:bg-vodaredDark text-white" data-testid="add-sub-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Subscriber
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard label="Active" value={stats.totals.active || 0} icon={UserCheck} color="text-electric" testid="stat-active" />
                    <StatCard label="Pending" value={stats.totals.pending || 0} icon={Clock} color="text-yellow-400" testid="stat-pending" />
                    <StatCard label="Opted Out" value={stats.totals.opted_out || 0} icon={UserMinus} color="text-white/50" testid="stat-optout" />
                    <StatCard label="Via SMS" value={stats.by_source?.sms || 0} icon={Send} color="text-blue-400" testid="stat-sms" />
                    <StatCard label="Via Web" value={stats.by_source?.web || 0} icon={Globe} color="text-purple-400" testid="stat-web" />
                </div>

                {/* Top Teams */}
                {stats.by_team && stats.by_team.length > 0 && (
                    <Card className="bg-paper border-border p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="h-4 w-4 text-electric" />
                            <h3 className="text-white font-heading font-bold text-sm uppercase tracking-wider">
                                Top Teams by Active Subscribers
                            </h3>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {stats.by_team.slice(0, 12).map((t, i) => (
                                <button
                                    key={i}
                                    onClick={() => setTeam(t.team === team ? "" : t.team)}
                                    className={`px-3 py-1.5 rounded border text-sm font-medium flex items-center gap-2 ${
                                        team === t.team
                                            ? "bg-electric/20 border-electric text-electric"
                                            : "bg-subtle border-border text-white/70 hover:border-electric/40"
                                    }`}
                                    data-testid={`team-chip-${i}`}
                                >
                                    <span>{t.team}</span>
                                    <Badge className="bg-electric/10 text-electric border-0">{t.active}</Badge>
                                </button>
                            ))}
                            {team && (
                                <button
                                    onClick={() => setTeam("")}
                                    className="text-white/50 hover:text-red-400 text-xs flex items-center gap-1"
                                >
                                    <X className="h-3 w-3" /> clear
                                </button>
                            )}
                        </div>
                    </Card>
                )}

                {/* Status Tabs */}
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide border-b border-border">
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setStatus(t.id)}
                            data-testid={`status-tab-${t.id}`}
                            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 flex items-center gap-2 transition-colors ${
                                status === t.id ? "border-electric text-electric" : "border-transparent text-white/60 hover:text-white"
                            }`}
                        >
                            <t.icon className="h-4 w-4" />
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Channel Tabs */}
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white/40 text-xs uppercase tracking-wider mr-1">Channel:</span>
                    {CHANNEL_TABS.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setChannel(c.id)}
                            data-testid={`channel-tab-${c.id}`}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                channel === c.id
                                    ? "bg-electric/20 border-electric text-electric"
                                    : "bg-subtle border-border text-white/60 hover:text-white"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <form onSubmit={handleSearch} className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                        placeholder="Search by phone number..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 bg-subtle border-border text-white h-9"
                        data-testid="sub-search-input"
                    />
                </form>

                {/* Table */}
                <Card className="bg-paper border-border overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-white/40">Loading...</div>
                    ) : items.length === 0 ? (
                        <div className="p-12 text-center">
                            <UserCheck className="h-12 w-12 text-white/20 mx-auto mb-3" />
                            <p className="text-white/60">No subscribers in this tab.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-subtle border-b border-border">
                                    <tr className="text-left text-white/60 text-xs uppercase">
                                        <th className="px-4 py-3">Phone</th>
                                        <th className="px-4 py-3">Team</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Source</th>
                                        <th className="px-4 py-3">Subscribed</th>
                                        <th className="px-4 py-3">Last SMS</th>
                                        <th className="px-4 py-3 text-center">SMS Sent</th>
                                        <th className="px-4 py-3 text-center">Engagement</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((s, idx) => (
                                        <tr key={s.id} className="border-b border-border/40 hover:bg-subtle/50" data-testid={`sub-row-${idx}`}>
                                            <td className="px-4 py-3 text-white font-mono text-xs">
                                                <button
                                                    onClick={() => setProfilePhone(s.phone_number)}
                                                    className="hover:text-electric underline-offset-2 hover:underline transition-colors"
                                                    data-testid={`open-profile-${s.phone_number}`}
                                                    title="View customer profile"
                                                >
                                                    {s.phone_number}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-white font-medium">{s.team}</td>
                                            <td className="px-4 py-3">
                                                <Badge
                                                    variant="outline"
                                                    className={`text-xs ${
                                                        s.status === "active" ? "bg-electric/10 text-electric border-electric/30"
                                                        : s.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                                                        : "bg-white/5 text-white/50 border-white/10"
                                                    }`}
                                                >
                                                    {s.status}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-white/60 text-xs">{s.source}</td>
                                            <td className="px-4 py-3 text-white/60 text-xs">{fmt(s.created_at)}</td>
                                            <td className="px-4 py-3 text-white/60 text-xs">{fmt(s.last_sms_at)}</td>
                                            <td className="px-4 py-3 text-center text-white font-mono">{s.total_sms_sent || 0}</td>
                                            <td className="px-4 py-3 text-center">
                                                <Badge className="bg-electric/10 text-electric border-0">{s.engagement_score || 0}</Badge>
                                            </td>
                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                {s.status !== "opted_out" && (
                                                    <Button size="sm" variant="ghost" onClick={() => handleOptOut(s.id)}
                                                        className="text-white/70 hover:text-yellow-400" title="Force opt-out"
                                                        data-testid={`optout-${idx}`}>
                                                        <UserMinus className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="ghost" onClick={() => handleDelete(s.id)}
                                                    className="text-white/70 hover:text-red-400" title="Delete"
                                                    data-testid={`delete-sub-${idx}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="bg-paper border-border text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading">Add Subscriber</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white/70">Phone Number</Label>
                            <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                                placeholder="+255712..." className="bg-subtle border-border text-white" data-testid="new-sub-phone" />
                        </div>
                        <div>
                            <Label className="text-white/70">Team</Label>
                            <Input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })}
                                placeholder="e.g. Arsenal, Simba, Yanga" className="bg-subtle border-border text-white" data-testid="new-sub-team" />
                        </div>
                        <p className="text-white/40 text-xs">
                            Admin-added subscribers are auto-activated — no SMS verification needed.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-white/60">Cancel</Button>
                        <Button onClick={handleAdd} className="bg-electric hover:bg-vodaredDark text-white" data-testid="save-sub-btn">
                            Add &amp; Activate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CustomerProfileSheet
                phone={profilePhone}
                open={!!profilePhone}
                onOpenChange={(o) => { if (!o) setProfilePhone(null); }}
            />
        </AdminLayout>
    );
};

const StatCard = ({ label, value, icon: Icon, color, testid }) => (
    <Card className="bg-paper border-border p-4" data-testid={testid}>
        <div className="flex items-center justify-between">
            <div>
                <div className="text-white/50 text-xs uppercase tracking-wider">{label}</div>
                <div className="text-white font-heading font-black text-3xl mt-1">{value}</div>
            </div>
            <div className={`w-10 h-10 rounded bg-subtle flex items-center justify-center ${color}`}>
                <Icon className="h-5 w-5" />
            </div>
        </div>
    </Card>
);

export default Subscriptions;
